import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import Button from 'tailwind-components/Button/Button';
import AgentFilesPanel from './components/AgentFilesPanel';
import AgentsRosterTable from './components/AgentsRosterTable';
import FilePreviewModal from './components/FilePreviewModal';
import FilesTable from './components/FilesTable';
import TableCard from 'tailwind-components/TableCard/TableCard';
import useClampPageToRange from 'hooks/useClampPageToRange';
import useCallReviewsOverview from './hooks/useCallReviewsOverview';

/**
 * Call Reviews weekly reports. the api decides what this page shows: agents get
 * their own reports, agency admins get the roster with each agent's reports inline.
 * the view discriminator comes from the response and is rendered as given.
 * @param setLocalLoader - injected by withLocalLoader to dismiss the page loader
 */
function CallReviewsPage({ setLocalLoader }) {
  const [page, setPage] = useState(1);
  const [expandedAgentId, setExpandedAgentId] = useState(null);
  // one page per agent, kept here so collapsing a row does not lose it
  const [agentPages, setAgentPages] = useState({});
  // the report being previewed, together with the agent it belongs to
  const [preview, setPreview] = useState(null);

  const user = useSelector(state => state.user.zohoData);
  // in the agent's own view the api sends no agent block, so the name comes from
  // the signed-in profile; the admin roster carries it per row
  const ownName = `${user?.First_Name ?? ''} ${user?.Last_Name ?? ''}`.trim();

  const {
    status,
    view,
    items,
    total,
    totalPages,
    isRefreshing,
    isRetrying,
    refreshError,
    refetch
  } = useCallReviewsOverview(page);

  useClampPageToRange({
    page,
    totalPages,
    itemCount: items.length,
    onPageChange: setPage
  });

  useEffect(() => {
    if (status !== 'loading') setLocalLoader(false);
  }, [status, setLocalLoader]);

  const handleToggleAgent = useCallback(agentId => {
    setExpandedAgentId(current => (current === agentId ? null : agentId));
  }, []);

  // the expanded row belongs to the page being left, so it closes with it
  const handleRosterPageChange = useCallback(nextPage => {
    setExpandedAgentId(null);
    setPage(nextPage);
  }, []);

  const handleAgentPageChange = useCallback((agentId, nextPage) => {
    setAgentPages(current => ({ ...current, [agentId]: nextPage }));
  }, []);

  const handleOpenFile = useCallback((file, agentName) => {
    setPreview({ file, agentName });
  }, []);

  const handleClosePreview = useCallback(() => setPreview(null), []);

  function renderExpandedAgent(agent) {
    return (
      <AgentFilesPanel
        agentId={agent.agent_id}
        agentName={`${agent.first_name} ${agent.last_name}`}
        page={agentPages[agent.agent_id] ?? 1}
        onPageChange={handleAgentPageChange}
        onOpenFile={handleOpenFile}
      />
    );
  }

  function renderContent() {
    if (status === 'loading') return null;

    if (status === 'no-access') {
      return (
        <p className='py-10 text-center text-[length:--font-size-body] text-grey'>
          You don&apos;t have access to this section.
        </p>
      );
    }

    if (status === 'error') {
      return (
        <div className='flex flex-col items-center gap-3 py-10'>
          <p className='text-[length:--font-size-body] text-grey'>
            Could not load reports.
          </p>
          <Button
            variant='default'
            outlined
            size='xsmall'
            rounded='medium'
            text='Retry'
            onClick={refetch}
          />
        </div>
      );
    }

    if (view === 'agents') {
      return (
        <TableCard
          title='Agents'
          count={total}
          countLabel='agent'
          page={page}
          totalPages={totalPages}
          onPageChange={handleRosterPageChange}
          isRefreshing={isRefreshing}
          isRetrying={isRetrying}
          refreshError={refreshError}
        >
          <AgentsRosterTable
            items={items}
            expandedAgentId={expandedAgentId}
            onToggleAgent={handleToggleAgent}
            renderExpanded={renderExpandedAgent}
          />
        </TableCard>
      );
    }

    return (
      <TableCard
        title='Reports'
        count={total}
        countLabel='report'
        page={page}
        totalPages={totalPages}
        onPageChange={setPage}
        isRefreshing={isRefreshing}
        isRetrying={isRetrying}
        refreshError={refreshError}
      >
        <FilesTable
          items={items}
          onOpenFile={handleOpenFile}
          agentName={ownName}
        />
      </TableCard>
    );
  }

  return (
    <div className='w-full pt-2'>
      <header className='mb-6 text-left'>
        <h1 className='m-0 text-left text-2xl font-semibold'>
          Call Reviews
        </h1>
        <p className='max-w-xl text-sm text-grey m-0'>
          Weekly call review reports, newest first.
        </p>
      </header>

      {renderContent()}

      <FilePreviewModal
        file={preview?.file ?? null}
        agentName={preview?.agentName}
        onClose={handleClosePreview}
      />
    </div>
  );
}

CallReviewsPage.propTypes = {
  setLocalLoader: PropTypes.func.isRequired
};

export default CallReviewsPage;
